import { ViewGroupEmployeeSheet } from './view-group-employee-sheet';
import { HttpResponse } from '@angular/common/http';
import { Injectable, OnDestroy, TemplateRef } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { Subscription } from 'rxjs/Subscription';
import { DepartmentAssetsModalComponent } from '../../common/employee-assets/department-assets-modal.component';
import { FullUser } from '../../../models/full-user.model';
import { Group } from '../../../models/group.model';
import { GroupContext } from '../../../models/group-context.model';
import { ReportType } from '../../../models/report-type.model';
import { ReportDatepickerComponent } from '../../../modals/report-datepicker.component';
import { DEFAULT_VALUES, GROUP_CONSTANTS, HTTP_STATUS_CODES, TIMEOUT_VALUES } from '../../../shared.constants/all-constants';
import { VectorValueComponent } from './vector-value/vector-value.component';
import { AddMemberPopupComponent } from './popups/add-member-popup/add-member-popup.component';
import { ChangeUserPasswordComponent } from './popups/change-user-password/change-user-password.component';
import { ClothingMarkerOverridesModalComponent } from './popups/clothing-marker-overrides/clothing-marker-overrides-modal.component';
import { EmployeeStandardOverridesModalComponent } from './popups/employee-standard-overrides/employee-standard-overrides-modal/employee-standard-overrides-modal.component';
import { SetHeadDeputyComponent, PendingHeadAssignment } from './popups/set-head-deputy/set-head-deputy.component';
import {
	CLOTHING_MARKER_OVERRIDES_WINDOW_CLASS,
	EMPLOYEE_STANDARD_OVERRIDES_WINDOW_CLASS,
	VIEW_GROUP_EMPTY_COUNT,
	VIEW_GROUP_MODAL_RESULT_OK,
	VIEW_GROUP_MODAL_SIZE_LARGE,
	VIEW_GROUP_RELOAD_TARGETS,
	VIEW_GROUP_SHIFT_REFRESH_DELAY
} from './view-group.constants';
import { ViewGroupIdentityState } from './view-group-identity.state';
import {
	ClosableOverlay,
	SelectableGroupMember,
	ViewGroupMemberDeletionRequest,
	ViewGroupMemberActionRequest,
	VIEW_GROUP_MEMBER_ACTIONS,
	ViewGroupReloadTarget,
	ViewGroupUiStateSnapshot,
	ViewGroupVectorUser
} from './view-group.types';
import { ViewGroupPlatform } from './view-group.platform';

const HEAD_APPOINTMENTS_WINDOW_CLASS = 'head-appointments-window';
const KARMA_WINDOW_CLASS = 'department-karma-dialog';
const MEMBER_ROW_TYPE = 'object';
const PHONE_MAX_WIDTH = 767;
const OFFBOARDING_BACKDROP: 'static' = 'static';

@Injectable()
export class ViewGroupUiCoordinator implements OnDestroy {
	readonly employeeSheet: ViewGroupEmployeeSheet;
	readonly stateChanges: Observable<ViewGroupUiStateSnapshot>;
	readonly reloadRequests: Observable<ViewGroupReloadTarget>;
	readonly memberDeletionRequests: Observable<ViewGroupMemberDeletionRequest>;
	readonly memberActionRequests: Observable<ViewGroupMemberActionRequest>;

	private readonly stateSubject = new Subject<ViewGroupUiStateSnapshot>();
	private readonly reloadSubject = new Subject<ViewGroupReloadTarget>();
	private readonly memberDeletionSubject = new Subject<ViewGroupMemberDeletionRequest>();
	private readonly memberActionSubject = new Subject<ViewGroupMemberActionRequest>();
	private mobWidth: number;
	private dataListVector: Object[] = [];
	private suc = false;
	private nodata = false;
	private started = false;
	private departmentAssetsModal: NgbModalRef;
	private offboardingModal: NgbModalRef;
	private headAssignment: PendingHeadAssignment;
	offboardingEmployee: FullUser = null;
	private readonly departmentAssetsLifecycle = new Subscription();

	constructor(
		private modalService: NgbModal,
		private identity: ViewGroupIdentityState,
		private platform: ViewGroupPlatform
	) {
		this.employeeSheet = new ViewGroupEmployeeSheet(this.modalService);
		this.stateChanges = this.stateSubject.asObservable();
		this.reloadRequests = this.reloadSubject.asObservable();
		this.memberDeletionRequests = this.memberDeletionSubject.asObservable();
		this.memberActionRequests = this.memberActionSubject.asObservable();
	}

	start(contextChanges?: Observable<void>, hostDestroyed?: Observable<void>): void {
		if (this.started) {
			return;
		}
		this.started = true;
		if (contextChanges) { this.departmentAssetsLifecycle.add(contextChanges.subscribe(() => this.closeEmployeeDialogs())); }
		if (hostDestroyed) { this.departmentAssetsLifecycle.add(hostDestroyed.subscribe(() => this.closeEmployeeDialogs())); }
		this.mobWidth = this.platform.viewportWidth();
		this.publishState();
		this.platform.bindResize(() => this.updateViewport());
	}

	select(member: SelectableGroupMember): void {
		member.isUserSelected = !member.isUserSelected;
	}

	delete(member: string | number | ViewGroupVectorUser): void {
		this.requestMemberAction(member, VIEW_GROUP_MEMBER_ACTIONS.DELETE);
	}

	completeMemberAction(request: ViewGroupMemberActionRequest, context: GroupContext, group?: Group): void {
		if (!this.identity.group || this.identity.group.id !== request.viewGroupId || !context || context.groupId !== request.groupId) { return; }
		if (request.action === VIEW_GROUP_MEMBER_ACTIONS.DELETE && context.canManageMembership) {
			this.memberDeletionSubject.next({ userId: request.userId, groupId: request.groupId });
			return;
		}
		if (request.action === VIEW_GROUP_MEMBER_ACTIONS.SHIFT && context.canManageGroupSettings && group && group.id === request.groupId) {
			this.openShiftForGroup(request.userId, group);
		}
	}

	private requestMemberAction(member: string | number | ViewGroupVectorUser, action: string): void {
		if (!this.identity.hasCurrentGroupContext) { return; }
		const row = typeof member === MEMBER_ROW_TYPE ? member as ViewGroupVectorUser : null;
		const groupId = row ? row.user.mainGroup : this.identity.group.id;
		if (!groupId) { return; }
		const request = { userId: row ? row.user.id : member as string | number, groupId: groupId,
			viewGroupId: this.identity.group.id, action: action };
		if (groupId === this.identity.group.id) {
			this.completeMemberAction(request, this.identity.groupContext, this.identity.group);
			return;
		}
		if (this.identity.groupContext.subdepartments.some(department => department.id === groupId)) {
			this.memberActionSubject.next(request);
		}
	}

	completeMemberDeletion(response: HttpResponse<Object>): void {
		if (response.status === HTTP_STATUS_CODES.OK) {
			this.platform.reloadPage();
		}
	}

	openVector(activeUser: ViewGroupVectorUser): void {
		if (!activeUser || activeUser.canEditVector !== true) { return; }
		const modalRef = this.modalService.open(VectorValueComponent);
		modalRef.componentInstance.action(activeUser);
		modalRef.result.then(result => {
			if (result === VIEW_GROUP_MODAL_RESULT_OK) {
				this.requestReload(VIEW_GROUP_RELOAD_TARGETS.CURRENT_GROUP);
			}
		});
	}

	openOwnReport(): void {
		const modalRef = this.modalService.open(ReportDatepickerComponent);
		modalRef.componentInstance.reportType = ReportType.BY_USER;
		modalRef.componentInstance.userId = this.identity.user.id;
	}

	openKarmaReport(action: string, userId: number): void {
		if (!this.canManageCurrentGroup() || String(userId) === this.identity.user.id) { return; }
		const modalRef = this.modalService.open(ReportDatepickerComponent, {windowClass: KARMA_WINDOW_CLASS});
		modalRef.componentInstance.reportType = ReportType.BY_KARMA;
		modalRef.componentInstance.actionTWO(action, userId);
		modalRef.result.then(result => {
			if (result === VIEW_GROUP_MODAL_RESULT_OK) {
				this.showSuccess(TIMEOUT_VALUES.DEFAULT_DELAY);
			}
		}, () => undefined); // Dismissal cancels the edit without a success message.
	}

	openDayOffReport(userId: number): void {
		if (!this.canManageCurrentGroup()) { return; }
		const modalRef = this.modalService.open(ReportDatepickerComponent);
		modalRef.componentInstance.reportType = ReportType.BY_DAY_OFF;
		modalRef.componentInstance.userId = userId;
		modalRef.componentInstance.rep = DEFAULT_VALUES.NOREP_FLAG;
		modalRef.componentInstance.action(GROUP_CONSTANTS.CHOOSE_DAYS_OFF);
		modalRef.result.then(result => this.handleSuccessResult(result));
	}

	openShiftReport(member: string | number | ViewGroupVectorUser): void {
		this.requestMemberAction(member, VIEW_GROUP_MEMBER_ACTIONS.SHIFT);
	}

	private openShiftForGroup(userId: string | number, group: Group): void {
		const modalRef = this.modalService.open(ReportDatepickerComponent);
		modalRef.componentInstance.reportType = ReportType.SHIFT;
		modalRef.componentInstance.userId = userId;
		modalRef.componentInstance.group = group;
		modalRef.componentInstance.action(GROUP_CONSTANTS.SHIFT_WINDOW);
		modalRef.result.then(result => this.handleShiftResult(result));
	}

	openPassword(user: FullUser, overlay: ClosableOverlay): void {
		overlay.close();
		const modalRef = this.modalService.open(ChangeUserPasswordComponent);
		modalRef.componentInstance.setUserPass(user);
	}

	openDepartmentUserReport(userId: number, overlay?: ClosableOverlay): void {
		if (!this.identity.hasCurrentGroupContext || !this.identity.groupContext.canReadSalaryReport) {
			return;
		}
		if (overlay) {
			overlay.close();
		}
		const modalRef = this.modalService.open(ReportDatepickerComponent);
		modalRef.componentInstance.reportType = ReportType.BY_USER_DEP;
		modalRef.componentInstance.userId = userId;
	}

	openAddMember(): void {
		if (!this.identity.hasCurrentGroupContext || !this.identity.groupContext.canManageMembership) {
			return;
		}
		const modalRef = this.modalService.open(AddMemberPopupComponent);
		modalRef.componentInstance.setGroup(this.identity.group);
	}

	openSetHeadDeputy(): void {
		if (!this.identity.hasCurrentGroupContext ||
			!(this.identity.groupContext.canManageGroupSettings || this.identity.groupContext.canAssignHead)) {
			return;
		}
		this.reconcileHeadAssignment();
		if (!this.headAssignment) {
			this.headAssignment = {actorId: this.identity.user.id, groupId: this.identity.group.id};
		}
		let editor: SetHeadDeputyComponent;
		const modalRef = this.modalService.open(SetHeadDeputyComponent, {
			windowClass: HEAD_APPOINTMENTS_WINDOW_CLASS,
			beforeDismiss: () => !editor || editor.isLoaded && !editor.taskSubmitting
		});
		modalRef.componentInstance.setGroup(this.identity.group);
		modalRef.componentInstance.pendingAssignment = this.headAssignment;
		modalRef.componentInstance.setShowHeadFields(this.identity.isAdmin);
		editor = modalRef.componentInstance;
		this.refreshAfterHeadAssignment(modalRef, this.identity.group.id);
	}

	private refreshAfterHeadAssignment(modalRef: NgbModalRef, groupId: string): void {
		const editor: SetHeadDeputyComponent = modalRef.componentInstance;
		const reload = () => {
			if (editor.didSave && this.identity.group && this.identity.group.id === groupId) {
				this.requestReload(VIEW_GROUP_RELOAD_TARGETS.CURRENT_GROUP);
			}
		};
		modalRef.result.then(reload, reload);
	}

	get hasPendingHeadAssignment(): boolean {
		return !!this.headAssignment && !!this.headAssignment.payload && !!this.identity.user && !!this.identity.group &&
			this.headAssignment.actorId === this.identity.user.id && this.headAssignment.groupId === this.identity.group.id;
	}

	private reconcileHeadAssignment(): void {
		if (this.headAssignment && (!this.identity.user || !this.identity.group ||
			this.headAssignment.actorId !== this.identity.user.id || this.headAssignment.groupId !== this.identity.group.id)) {
			this.clearHeadAssignment();
		}
	}

	private clearHeadAssignment(): void {
		if (!this.headAssignment) { return; }
		this.headAssignment.payload = null;
		this.headAssignment.head = this.headAssignment.deputy = null;
		this.headAssignment = null;
	}

	openGroupReport(): void {
		if (!this.identity.hasCurrentGroupContext || !this.identity.groupContext.canReadSalaryReport) {
			return;
		}
		const modalRef = this.modalService.open(ReportDatepickerComponent);
		modalRef.componentInstance.reportType = ReportType.BY_GROUP;
		modalRef.componentInstance.groupId = this.identity.group;
		modalRef.componentInstance.groupName = this.identity.group.name;
	}

	openVectorReport(): void {
		if (!this.canManageCurrentGroup()) { return; }
		this.dataListVector = [];
		this.publishState();
		const modalRef = this.modalService.open(ReportDatepickerComponent);
		modalRef.componentInstance.reportType = ReportType.BY_VECTOR;
		modalRef.componentInstance.groupId = this.identity.group.id;
		modalRef.result.then(result => this.handleVectorResult(result));
	}

	openGroupLeave(): void {
		if (!this.canManageCurrentGroup()) { return; }
		const modalRef = this.modalService.open(ReportDatepickerComponent);
		modalRef.componentInstance.reportType = ReportType.BY_DAY_OFF_GROUP;
		modalRef.componentInstance.groupId = this.identity.group;
		modalRef.componentInstance.action(GROUP_CONSTANTS.TAKE_LEAVE_ON_GROUP);
		modalRef.result.then(result => this.handleSuccessResult(result));
	}

	openPersonalStandards(): void {
		if (!this.identity.hasCurrentGroupContext || !this.identity.groupContext.canManagePersonalStandards) {
			return;
		}
		const modalRef = this.modalService.open(
			EmployeeStandardOverridesModalComponent,
			{ size: VIEW_GROUP_MODAL_SIZE_LARGE, windowClass: EMPLOYEE_STANDARD_OVERRIDES_WINDOW_CLASS }
		);
		modalRef.componentInstance.groupId = this.identity.group.id;
		modalRef.componentInstance.departments = [
			{ id: this.identity.group.id, name: this.identity.group.name },
			this.identity.groupContext.parentDepartment
		].filter(Boolean);
	}

	openClothingMarkers(): void {
		if (!this.identity.hasCurrentGroupContext || !this.identity.groupContext.canManageClothingMarkers) {
			return;
		}
		const modalRef = this.modalService.open(
			ClothingMarkerOverridesModalComponent,
			{ size: VIEW_GROUP_MODAL_SIZE_LARGE, windowClass: CLOTHING_MARKER_OVERRIDES_WINDOW_CLASS }
		);
		modalRef.componentInstance.group = this.identity.group;
	}

	openDepartmentAssets(): void {
		if (this.departmentAssetsModal || !this.identity.hasCurrentGroupContext ||
			this.identity.group.parentDepartmentId || this.identity.groupContext.parentDepartment ||
			this.identity.groupContext.canReadDepartmentAssets !== true) { return; }
		const modalRef = this.modalService.open(DepartmentAssetsModalComponent, { size: VIEW_GROUP_MODAL_SIZE_LARGE });
		this.departmentAssetsModal = modalRef;
		modalRef.componentInstance.departmentId = this.identity.group.id;
		modalRef.componentInstance.departmentName = this.identity.group.name;
		const release = () => { if (this.departmentAssetsModal === modalRef) { this.departmentAssetsModal = null; } };
		modalRef.result.then(release, release);
	}

	private closeDepartmentAssets(): void {
		if (!this.departmentAssetsModal) { return; }
		this.departmentAssetsModal.dismiss();
		this.departmentAssetsModal = null;
	}

	canOpenOffboarding(member: ViewGroupVectorUser): boolean {
		if (!this.canManageCurrentGroup() || !this.identity.user || !member || !member.user ||
			!member.user.id || member.user.id === this.identity.user.id || this.identity.group.parentDepartmentId ||
			!this.identity.group.head || this.identity.group.head.id !== this.identity.user.id) { return false; }
		return member.user.mainGroup === this.identity.group.id ||
			this.identity.groupContext.subdepartments.some(department => department.id === member.user.mainGroup);
	}

	openOffboarding(member: ViewGroupVectorUser, content: TemplateRef<Object>): void {
		if (this.offboardingModal || !this.canOpenOffboarding(member)) { return; }
		this.offboardingEmployee = member.user;
		const modalRef = this.modalService.open(content, { backdrop: OFFBOARDING_BACKDROP, keyboard: false });
		this.offboardingModal = modalRef;
		const release = () => {
			if (this.offboardingModal === modalRef) {
				this.offboardingModal = null;
				this.offboardingEmployee = null;
			}
		};
		modalRef.result.then(release, release);
	}

	private closeEmployeeDialogs(): void {
		this.reconcileHeadAssignment();
		this.employeeSheet.close();
		this.closeDepartmentAssets();
		if (this.offboardingModal) { this.offboardingModal.dismiss(); }
		this.offboardingModal = null;
		this.offboardingEmployee = null;
	}

	ngOnDestroy(): void {
		this.clearHeadAssignment();
		this.closeEmployeeDialogs();
		this.departmentAssetsLifecycle.unsubscribe();
		this.stateSubject.complete();
		this.reloadSubject.complete();
		this.memberDeletionSubject.complete();
		this.memberActionSubject.complete();
	}

	private canManageCurrentGroup(): boolean {
		return this.identity.hasCurrentGroupContext && this.identity.groupContext.canManageGroupSettings;
	}

	private updateViewport(): void {
		this.mobWidth = this.platform.viewportWidth();
		if (this.mobWidth > PHONE_MAX_WIDTH) { this.employeeSheet.close(); }
		this.publishState();
		this.platform.refreshView();
	}

	private handleSuccessResult(result: string): void {
		if (result === VIEW_GROUP_MODAL_RESULT_OK) {
			this.showSuccess(TIMEOUT_VALUES.DEFAULT_DELAY);
		}
	}

	private handleShiftResult(result: string): void {
		if (result !== VIEW_GROUP_MODAL_RESULT_OK) {
			return;
		}
		this.requestReload(VIEW_GROUP_RELOAD_TARGETS.CURRENT_GROUP);
		this.platform.schedule(() => this.finishShiftReport(), VIEW_GROUP_SHIFT_REFRESH_DELAY);
	}

	private finishShiftReport(): void {
		if (this.identity.isHead && this.identity.isAdmin) {
			this.requestReload(VIEW_GROUP_RELOAD_TARGETS.AVAILABLE_GROUPS);
		}
		this.showSuccess(TIMEOUT_VALUES.DEFAULT_DELAY);
	}

	private handleVectorResult(result: string): void {
		if (result !== VIEW_GROUP_MODAL_RESULT_OK) {
			return;
		}
		this.dataListVector = this.platform.readVectorData();
		this.publishState();
		if (this.dataListVector.length === VIEW_GROUP_EMPTY_COUNT) {
			this.showNoData();
			return;
		}
		this.showSuccess(TIMEOUT_VALUES.LONG_DELAY);
	}

	private showSuccess(delay: number): void {
		this.suc = true;
		this.publishState();
		this.platform.schedule(() => {
			this.suc = false;
			this.publishState();
		}, delay);
	}

	private showNoData(): void {
		this.nodata = true;
		this.publishState();
		this.platform.schedule(() => {
			this.nodata = false;
			this.publishState();
		}, TIMEOUT_VALUES.LONG_DELAY);
	}

	private publishState(): void {
		this.stateSubject.next({
			mobWidth: this.mobWidth,
			dataListVector: this.dataListVector,
			suc: this.suc,
			nodata: this.nodata
		});
	}

	private requestReload(target: ViewGroupReloadTarget): void {
		this.reloadSubject.next(target);
	}
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/views/view-group/view-group-ui.coordinator.ts